import { isTextManagementAvailable } from "../utils/environmentUtils";

export interface SectionTool {
    /** i18n key under `header.navBar.*` (preferred), or a literal `label` for tools without a key. */
    labelKey?: string;
    label?: string;
    icon: string;
    to: string;
}

export interface Section {
    key: string;
    title: string;
    icon: string;
    description: string;
    /** Route of the team's overview page. */
    to: string;
    tools: SectionTool[];
}

// Single source of truth for the team sections (Support / PE / Marketing / Dev). Consumed by the home landing,
// the per-team overview pages and the header nav, so the three never drift. Section titles/descriptions
// are literals (internal tool); tool labels reuse the existing header.navBar.* keys where they exist.
//
// Sections are ORGANISATION, NOT ACCESS CONTROL. They group tools by who typically uses them so the
// app is navigable; every one of them is reachable by every admin user, and the only real gate is the
// admin role the whole app sits behind (plus isTextManagementAvailable() below, which is about the
// environment, not the person). A section named after a team reads like a permission boundary — it
// is not one, and nothing here should be relied on as though it were.
export function getSections(): Section[] {
    return [
        {
            key: "support",
            title: "Support",
            icon: "pi pi-fw pi-users",
            description: "Trainer, participant, project and billing tooling, plus the profile-generation status page.",
            to: "/support",
            tools: [
                { labelKey: "header.navBar.participants", icon: "pi pi-fw pi-file", to: "/participants" },
                { labelKey: "header.navBar.projects", icon: "pi pi-fw pi-pencil", to: "/projects" },
                { labelKey: "header.navBar.trainer", icon: "pi pi-fw pi-user", to: "/trainers" },
                { labelKey: "header.navBar.billing", icon: "pi pi-fw pi-receipt", to: "/billing" },
                { label: "Profile Generation", icon: "pi pi-fw pi-sync", to: "/profile-generation" },
            ],
        },
        {
            key: "pe",
            title: "PE",
            icon: "pi pi-fw pi-language",
            description: "Product language activation and text maintenance across languages.",
            to: "/pe",
            tools: [
                { labelKey: "header.navBar.products", icon: "pi pi-fw pi-cog", to: "/language-activation" },
                // Text management is only available in non-production environments.
                ...(isTextManagementAvailable()
                    ? [
                          { labelKey: "header.navBar.textMaintenance", icon: "pi pi-fw pi-file-edit", to: "/text-maintenance" },
                          { labelKey: "header.navBar.textDiffs", icon: "pi pi-fw pi-code", to: "/text-diffs" },
                          { labelKey: "header.navBar.textSearch", icon: "pi pi-fw pi-search", to: "/text-search" },
                          { labelKey: "header.navBar.textDependencies", icon: "pi pi-fw pi-sitemap", to: "/text-dependencies" },
                      ]
                    : []),
            ],
        },
        {
            key: "marketing",
            title: "Marketing",
            icon: "pi pi-fw pi-bullseye",
            description: "Campaign content on the customer-facing pages — the start page banners.",
            to: "/marketing",
            tools: [
                // Moved out of Dev (RDT-359): marketing owns what the start page advertises, and
                // putting it under a section named after the people who use it is what makes the
                // area findable. The notification banners stay in Dev — those are IT's maintenance
                // notices, a different job that happens to use the same window and audience rules.
                { labelKey: "startPageBanners.title", icon: "pi pi-fw pi-images", to: "/start-page-banners" },
            ],
        },
        {
            // Its own section (RDT-435): these numbers serve statistics and invoicing across teams,
            // not the daily work of one of them, so they sit between the business sections and Dev.
            key: "statistics",
            title: "Statistics",
            icon: "pi pi-fw pi-chart-bar",
            description: "How many profiles of each product were generated, real and test profiles apart.",
            to: "/statistics",
            tools: [{ labelKey: "header.navBar.profileStatistics", icon: "pi pi-fw pi-chart-bar", to: "/statistics/profiles" }],
        },
        {
            key: "dev",
            title: "Dev",
            icon: "pi pi-fw pi-wrench",
            description: "The Dev Cockpit — MRs, versions, tickets, pending profiles/participants and logs.",
            to: "/dev",
            tools: [
                // Profile generation lives in the Cockpit now; it stays under Support as its own page.
                { label: "Cockpit", icon: "pi pi-fw pi-desktop", to: "/dev/cockpit" },
                { label: "Release Notes", icon: "pi pi-fw pi-book", to: "/dev/release-notes" },
                // Under Dev rather than PE: IT maintains the banners, and unlike the PE text
                // tooling this one exists in the production admin too (RDT-339).
                { labelKey: "banners.title", icon: "pi pi-fw pi-megaphone", to: "/banners" },
            ],
        },
    ];
}
